import { FC } from 'react'

import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useGenresAndLanguagesAnalytics } from 'src/Hooks/useGenresAndLanguagesAnalytics'
import { useLazyGetBudgetRangeQuery } from 'src/Redux/spotifyBudgetRangeApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ProperGenreOrLanguage, SelectedLanguage } from 'src/Types/tracks'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { useUpdateSpotifyCampaignLanguagesMutation } from 'src/Redux/spotify-campaignApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'

const Container = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 16px;
  position: fixed;
  bottom: 0;
  background-color: ${({ theme }) => theme.color.white};
  padding: 8px 16px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  div:last-child {
    width: fit-content;
    .ant-btn.ant-btn-default {
      padding: 0 40px;
    }
  }
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

interface Props {
  selectedLanguage: SelectedLanguage | null
  targetLanguage?: number
  onBackClick?: () => void
  campaignLanguages: ProperGenreOrLanguage[]
}

export const LanguagesModalSpotifyButton: FC<Props> = ({
  selectedLanguage,
  targetLanguage,
  onBackClick,
  campaignLanguages,
}) => {
  const { number: campaignId } = useParams<{ number: string }>()
  const [updateSpotifyCampaignLanguages, { isLoading }] =
    useUpdateSpotifyCampaignLanguagesMutation()
  const [getCampaignBudgetRange] = useLazyGetBudgetRangeQuery()

  const { handleLanguageAddedEvent } = useGenresAndLanguagesAnalytics()

  const { campaignDetails } = useSpotifyCampaignStatus()
  const { setSearch } = useCustomSearchParams()

  const selectedLanguageOnlySavedInCampaign =
    targetLanguage === 0 &&
    !campaignLanguages.find((language) => language.id === ANY_LANGUAGE_ID)

  const isCurrentSameWithTarget =
    Boolean(campaignLanguages.find(({ id }) => id === targetLanguage)) ||
    selectedLanguageOnlySavedInCampaign

  const isCurrentSameWithSelected = Boolean(
    campaignLanguages.find(({ id }) => id === selectedLanguage?.value),
  )

  const isLanguageNotChanged =
    isCurrentSameWithTarget && isCurrentSameWithSelected

  const handleSave = (): void => {
    const languages =
      targetLanguage === 0
        ? [selectedLanguage?.value]
        : [selectedLanguage?.value, targetLanguage]

    handleLanguageAddedEvent({
      platformName: CampaignPlatformTypeEnum.SPOTIFY,
      languageId: String(selectedLanguage?.value),
      languageName: String(selectedLanguage?.label),
      isAnyLanguage: Boolean(languages.find((id) => id === ANY_LANGUAGE_ID)),
    })
    if (!campaignId) {
      return
    }
    updateSpotifyCampaignLanguages({
      id: campaignId,
      languageIds: languages.map((el) => Number(el)),
    })
      .unwrap()
      .then(() => {
        if (campaignDetails?.genres.length) {
          getCampaignBudgetRange(campaignId)
        }
        setSearch({ modal: null })
      })
      .catch(() => null)
  }

  return (
    <Container translate='no'>
      <PrimaryDottedButton onClick={onBackClick}>Go Back</PrimaryDottedButton>
      <StyledCTAButton
        disabled={!selectedLanguage || isLanguageNotChanged || isLoading}
        onClick={handleSave}
      >
        Add language
      </StyledCTAButton>
    </Container>
  )
}
